import { useState } from "react";

import { useHistory } from "react-router-dom";

import { Banner, Spinner, Text, toastError } from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import { getErrorMessage, useMutationRequest } from "~/v2/api/hooks";
import {
  enrollInFPSL,
  type PostAccountRequest,
  postAccount,
} from "~/v2/api/rest/account";
import { postAgreement } from "~/v2/api/rest/agreements";
import { AgreementBox, PDFAgreementModal } from "~/v2/components/form";
import { Path } from "~/v2/path";
import { FloatingButtons } from "../../../../components/common/FloatingButtons";
import type { IRAOnboardingProps } from "../index";
import { SuccessModal } from "./SuccessModal";

const CUSTOMER_AGREEMENT_URL =
  "https://files.alpaca.markets/disclosures/library/AcctAppMarginAndCustAgmt.pdf";

const EQUITY_TRUST_AGREEMENT_URL =
  "https://files.alpaca.markets/disclosures/library/Equity+Trust+Company+Trust+Agreement.pdf";

const STOCK_LENDING_AGREEMENT_URL =
  "https://files.alpaca.markets/disclosures/Alpaca_Master+Securities+Lending+Agreement+for+Fully+Paid+Securities+Lending_v01.2025.03.pdf";

const LIMITED_MARGIN_AGREEMENT_URL =
  "https://files.alpaca.markets/disclosures/library/MarginDiscStmt.pdf";

type AgreementState = {
  isOpen: boolean;
  isAccepted: boolean;
};

type State = {
  customerAgreement: AgreementState;
  equityTrustAgreement: AgreementState;
  stockLendingAgreement: AgreementState;
  limitedMarginAgreement: AgreementState;
};

export const Agreements = ({
  tradeAccount,
  tradeAccountDetails,
  subType,
  beneficiaries,
  isStockLendingEnabled,
  onSaveSessionState,
  onDeleteSessionState,
}: IRAOnboardingProps) => {
  const history = useHistory();
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [state, setState] = useState<State>({
    customerAgreement: {
      isOpen: false,
      isAccepted: false,
    },
    equityTrustAgreement: {
      isOpen: false,
      isAccepted: false,
    },
    stockLendingAgreement: {
      isOpen: false,
      isAccepted: false,
    },
    limitedMarginAgreement: {
      isOpen: false,
      isAccepted: false,
    },
  });

  const onOpenAgreement = (agreement: keyof State) => () =>
    setState((prev) => ({
      ...prev,
      [agreement]: { ...prev[agreement], isOpen: true },
    }));

  const onCloseAgreement = (agreement: keyof State) => () =>
    setState((prev) => ({
      ...prev,
      [agreement]: { ...prev[agreement], isOpen: false },
    }));

  const onAgreeAgreement = (agreement: keyof State) => () =>
    setState((prev) => ({
      ...prev,
      [agreement]: { ...prev[agreement], isAccepted: true, isOpen: false },
    }));

  const onSuccessModalContinue = () => {
    history.push(Path.ROUTE_DASHBOARD);
    onDeleteSessionState();
  };

  const mslaAgreementMutation = useMutationRequest(
    async (iraAccountId: string) => {
      const ownerId = tradeAccountDetails.owner_id;
      if (!ownerId) {
        throw new Error("Missing owner id for agreement signing");
      }
      return postAgreement({
        ownerId,
        agreement: "master_securities_lending_agreement",
        accountId: iraAccountId,
      });
    },
    {
      onSuccess: (_, iraAccountId) => {
        enrollInFPSLMutation.mutate(iraAccountId);
        setIsSuccessModalOpen(true);
      },
      onError: (error) => {
        // Their IRA account was created so we can show the success modal
        setIsSuccessModalOpen(true);

        // display error message to user
        const errorMessage = getErrorMessage(error);
        console.error(
          "Error, failed to sign Master Securities Lending Agreement",
          error,
        );
        toastError(
          `Error signing Master Securities Lending Agreement: ${errorMessage}`,
        );
      },
    },
  );

  const enrollInFPSLMutation = useMutationRequest(
    (iraAccountId: string) => enrollInFPSL(iraAccountId),
    {
      onSuccess: () => {
        AmplitudeProvider.dispatch("algodash_enrolled_fpsl");
        setIsSuccessModalOpen(true);
      },
      onError: (error) => {
        // Their account was created so we can show the success modal
        setIsSuccessModalOpen(true);

        // display error message to user
        const errorMessage = getErrorMessage(error);
        console.error("Error enrolling in FPSL", error);
        toastError(`Error enrolling in stock lending program: ${errorMessage}`);
      },
    },
  );

  const createIRAAccountMutation = useMutationRequest(
    (data: Partial<PostAccountRequest>) => postAccount(data),
    {
      onSuccess: (iraAccount) => {
        if (isStockLendingEnabled) {
          mslaAgreementMutation.mutate(iraAccount.id);
        } else {
          setIsSuccessModalOpen(true);
        }
      },
      onError: (error) => {
        const errorMessage = getErrorMessage(error);
        console.error("Error creating account", error);
        toastError(`Error creating IRA account: ${errorMessage}`);
      },
    },
  );

  const onSubmitClicked = () => {
    if (!subType) {
      toastError("Account type is required");
      onSaveSessionState({
        step: "type",
      });
      return;
    }

    const firstName = tradeAccountDetails.given_name;
    const lastName = tradeAccountDetails.family_name;
    const country = tradeAccountDetails.country_of_tax_residence;
    const ownerId = tradeAccountDetails.owner_id;

    if (!firstName || !lastName || !country || !ownerId) {
      toastError(
        "Account holder details are incomplete. Please update your profile and try again.",
      );
      return;
    }

    createIRAAccountMutation.mutate({
      first_name: firstName,
      last_name: lastName,
      country,
      account_type: "ira",
      account_sub_type: subType,
      primary_account_holder_id: ownerId,
      beneficiaries: beneficiaries,
      investment_objective: tradeAccount.investment_objective ?? undefined,
    });
  };

  const requiredAgreements = Object.entries(state)
    .filter(([key]) =>
      isStockLendingEnabled ? true : key !== "stockLendingAgreement",
    )
    .map(([_key, agreement]) => agreement);

  const isAlmostBannerDisplayed = requiredAgreements.some(
    (agreement) => agreement.isAccepted,
  );

  const isEveryAgreementAccepted = requiredAgreements.every(
    (agreement) => agreement.isAccepted,
  );

  const {
    customerAgreement,
    equityTrustAgreement,
    stockLendingAgreement,
    limitedMarginAgreement,
  } = state;

  const isSubmitButtonDisabled = !requiredAgreements.every(
    (agreement) => agreement.isAccepted,
  );

  const isSubmitLoading =
    createIRAAccountMutation.isPending ||
    mslaAgreementMutation.isPending ||
    enrollInFPSLMutation.isPending;

  return (
    <>
      {isSubmitLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-white/70">
          <Spinner />
        </div>
      )}
      <div className="flex h-full flex-col gap-8">
        <div className="flex h-full flex-col gap-4">
          {isAlmostBannerDisplayed && !isEveryAgreementAccepted && (
            <Banner
              variant="info"
              title="You are almost there"
              message="Complete the missing agreements to finalize your account."
            />
          )}
          <AgreementBox
            isAccepted={customerAgreement.isAccepted}
            label="Alpaca Customer agreement"
            onOpen={onOpenAgreement("customerAgreement")}
          />
          <AgreementBox
            isAccepted={equityTrustAgreement.isAccepted}
            label="Equity Trust Agreement"
            onOpen={onOpenAgreement("equityTrustAgreement")}
          />
          {isStockLendingEnabled && (
            <AgreementBox
              isAccepted={stockLendingAgreement.isAccepted}
              label="Master Securities Lending Agreement for Fully Paid Securities Lending"
              onOpen={onOpenAgreement("stockLendingAgreement")}
            />
          )}
          <AgreementBox
            isAccepted={limitedMarginAgreement.isAccepted}
            label="Limited Margin Agreement"
            onOpen={onOpenAgreement("limitedMarginAgreement")}
          />

          <PDFAgreementModal
            isOpen={customerAgreement.isOpen}
            title="Alpaca Customer agreement"
            url={CUSTOMER_AGREEMENT_URL}
            onCancel={onCloseAgreement("customerAgreement")}
            onAgree={onAgreeAgreement("customerAgreement")}
            hasAgreed={customerAgreement.isAccepted}
          />
          <PDFAgreementModal
            isOpen={equityTrustAgreement.isOpen}
            title="Equity Trust Agreement"
            url={EQUITY_TRUST_AGREEMENT_URL}
            onCancel={onCloseAgreement("equityTrustAgreement")}
            onAgree={onAgreeAgreement("equityTrustAgreement")}
            hasAgreed={equityTrustAgreement.isAccepted}
          />
          {isStockLendingEnabled && (
            <PDFAgreementModal
              isOpen={stockLendingAgreement.isOpen}
              title="Master Securities Lending Agreement for Fully Paid Securities Lending"
              url={STOCK_LENDING_AGREEMENT_URL}
              onCancel={onCloseAgreement("stockLendingAgreement")}
              onAgree={onAgreeAgreement("stockLendingAgreement")}
              hasAgreed={stockLendingAgreement.isAccepted}
            />
          )}
          <PDFAgreementModal
            isOpen={limitedMarginAgreement.isOpen}
            title="Limited Margin Agreement"
            url={LIMITED_MARGIN_AGREEMENT_URL}
            onCancel={onCloseAgreement("limitedMarginAgreement")}
            onAgree={onAgreeAgreement("limitedMarginAgreement")}
            hasAgreed={limitedMarginAgreement.isAccepted}
          />

          <SuccessModal
            isOpen={isSuccessModalOpen}
            onContinue={onSuccessModalContinue}
          />
        </div>

        <div>
          <Text className="text-gray-400 text-xs">
            Securities Brokerage services are provided by Alpaca Securities ,
            member FINRA/SIPC, a wholly-owned subsidiary of AlpacaDB, Inc.
            Technology and services are offered by AlpacaDB, Inc. Cryptocurrency
            services are made available by Alpaca Crypto LLC ("Alpaca Crypto"),
            a FinCEN registered money services business (NMLS # 2160858), and a
            wholly-owned subsidiary of AlpacaDB, Inc. Alpaca Crypto is not a
            member of SIPC or FINRA. Cryptocurrencies are not stocks and your
            cryptocurrency investments are not protected by either FDIC or SIPC.
            Please see the Disclosure Library for more information.
            <br /> <br />
            This is not an offer, solicitation of an offer, or advice to buy or
            sell securities or cryptocurrencies, or open a brokerage account or
            cryptocurrency account in any jurisdiction where Alpaca Securities,
            Alpaca Crypto, are not registered or licensed, as applicable.
          </Text>
        </div>
        <FloatingButtons
          fixedOnBottom
          className="bg-white"
          next={{
            disabled: isSubmitButtonDisabled,
            label: "Finish Setup",
            onClick: onSubmitClicked,
            loading: isSubmitLoading,
          }}
          back={{
            onClick: () => {
              onSaveSessionState({
                step: "stock_lending",
              });
            },
            disabled: isSubmitLoading,
          }}
        />
      </div>
    </>
  );
};
